import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle, CardBody } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Table, TableHead, TableBody, TableRow, TableCell } from './ui/Table';
import { Plus, Trash2, TrendingUp, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { UsageTopUpType } from '../types/admin';

const TOP_UP_TYPE_LABELS: Record<UsageTopUpType, string> = {
  scans: 'Scans',
  pr_reviews: 'PR Reviews',
  reverifications: 'Reverifications',
  repos: 'Repositories',
};

interface AccountUsageTopUpsProps {
  accountId: string;
}

const AccountUsageTopUps: React.FC<AccountUsageTopUpsProps> = ({ accountId }) => {
  const queryClient = useQueryClient();
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [topUpType, setTopUpType] = useState<UsageTopUpType>('scans');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpDuration, setTopUpDuration] = useState('');
  const [topUpReason, setTopUpReason] = useState('');
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const { data: topUpsData, isLoading } = useQuery({
    queryKey: ['account-top-ups', accountId],
    queryFn: () => adminApiClient.getUsageTopUps(accountId),
    enabled: !!accountId,
  });

  const grantMutation = useMutation({
    mutationFn: (data: { type: UsageTopUpType; amount: number; duration_months?: number; reason: string }) =>
      adminApiClient.grantUsageTopUp(accountId, data),
    onSuccess: () => {
      toast.success('Usage top-up granted');
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['account-top-ups', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-plan', accountId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to grant top-up');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (topUpId: string) =>
      adminApiClient.revokeUsageTopUp(accountId, topUpId),
    onSuccess: () => {
      toast.success('Usage top-up revoked');
      queryClient.invalidateQueries({ queryKey: ['account-top-ups', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-plan', accountId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to revoke top-up');
    },
    onSettled: () => setRevokingId(null),
  });

  const handleRevoke = (topUpId: string, topUpType: string, amount: number) => {
    const label = TOP_UP_TYPE_LABELS[topUpType as UsageTopUpType] || topUpType;
    if (!window.confirm(`Revoke +${amount} ${label} top-up? This will reduce the account's available capacity.`)) {
      return;
    }
    setRevokingId(topUpId);
    revokeMutation.mutate(topUpId);
  };

  const resetForm = () => {
    setShowGrantForm(false);
    setTopUpType('scans');
    setTopUpAmount('');
    setTopUpDuration('');
    setTopUpReason('');
  };

  const handleGrant = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseInt(topUpAmount, 10);
    if (isNaN(amount) || amount < 1) {
      toast.error('Amount must be at least 1');
      return;
    }
    if (!topUpReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    const duration = topUpDuration ? parseInt(topUpDuration, 10) : undefined;
    if (duration !== undefined && (isNaN(duration) || duration < 1)) {
      toast.error('Duration must be at least 1 month');
      return;
    }
    grantMutation.mutate({
      type: topUpType,
      amount,
      duration_months: duration,
      reason: topUpReason.trim(),
    });
  };

  const topUps = topUpsData?.top_ups || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Active Top-ups Table */}
      <div className="lg:col-span-2">
        <Card padding="none">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                <CardTitle>Usage Top-ups ({topUps.length})</CardTitle>
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
            </div>
          ) : topUps.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Amount</TableCell>
                  <TableCell header>Expires</TableCell>
                  <TableCell header>Reason</TableCell>
                  <TableCell header>Granted</TableCell>
                  <TableCell header>{''}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topUps.map((topUp) => (
                  <TableRow key={topUp.id}>
                    <TableCell>
                      <Badge variant="info">
                        {TOP_UP_TYPE_LABELS[topUp.type] || topUp.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-green-400 font-medium">
                        +{topUp.amount}
                      </span>
                    </TableCell>
                    <TableCell>
                      {topUp.expires_at ? (
                        <span className="text-gray-300 text-sm">
                          {format(new Date(topUp.expires_at), 'MMM d, yyyy')}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">Never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400 text-sm truncate block max-w-[200px]" title={topUp.reason}>
                        {topUp.reason}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-gray-400 text-sm">
                        {format(new Date(topUp.granted_at), 'MMM d, yyyy')}
                      </div>
                      <div className="text-gray-600 text-xs">
                        {topUp.granted_by}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevoke(topUp.id, topUp.type, topUp.amount)}
                        loading={revokingId === topUp.id}
                        disabled={revokingId !== null}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center text-gray-400">
              No active top-ups
            </div>
          )}
        </Card>
      </div>

      {/* Grant Top-up Form */}
      <Card>
        <div className="p-6 border-b border-gray-700">
          <CardTitle>Grant Top-up</CardTitle>
        </div>
        <CardBody>
          {showGrantForm ? (
            <form onSubmit={handleGrant} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={topUpType}
                  onChange={(e) => setTopUpType(e.target.value as UsageTopUpType)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="scans">Scans</option>
                  <option value="pr_reviews">PR Reviews</option>
                  <option value="reverifications">Reverifications</option>
                  <option value="repos">Repositories</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  min="1"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="10"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Duration (months, optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={topUpDuration}
                  onChange={(e) => setTopUpDuration(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Leave empty for permanent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  value={topUpReason}
                  onChange={(e) => setTopUpReason(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Customer support bonus"
                  required
                />
              </div>
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  variant="primary"
                  loading={grantMutation.isPending}
                  disabled={grantMutation.isPending}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Grant
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">Add usage capacity to this account</p>
              <Button variant="primary" onClick={() => setShowGrantForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Grant Top-up
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AccountUsageTopUps;
